import { useCallback, useState } from "react";
import { Plus, Minus, Pencil, Type, FileText, Check, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type {
    IRefinementDiff,
    IAddTopicEntry,
    IRemoveTopicEntry,
    IAddClauseEntry,
    IRemoveClauseEntry,
    IUpdateTopicRationaleEntry,
} from "@/api/clause-checklists/checklist-generation.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type OperationType =
    | "add_topic"
    | "remove_topic"
    | "add_clauses"
    | "remove_clauses"
    | "update_rationale"
    | "update_name"
    | "update_description";

interface DiffOperation {
    id: string;
    type: OperationType;
    label: string;
    detail?: string;
    checked: boolean;
}

interface DiffPreviewProps {
    diff: IRefinementDiff;
    topicNames?: Record<number, string>;
    onApply: (filteredDiff: IRefinementDiff) => void;
    onReject: () => void;
    isApplying?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const OP_CONFIG: Record<
    OperationType,
    {
        icon: typeof Plus;
        color: string;
        rowTint: string;
        badge: string;
        badgeVariant: "default" | "destructive" | "outline" | "secondary";
    }
> = {
    add_topic: {
        icon: Plus,
        color: "text-emerald-600 dark:text-emerald-400",
        rowTint: "bg-emerald-50/60 dark:bg-emerald-950/20",
        badge: "Add Topic",
        badgeVariant: "default",
    },
    remove_topic: {
        icon: Minus,
        color: "text-red-500 dark:text-red-400",
        rowTint: "bg-red-50/60 dark:bg-red-950/20",
        badge: "Remove",
        badgeVariant: "destructive",
    },
    add_clauses: {
        icon: Plus,
        color: "text-emerald-600 dark:text-emerald-400",
        rowTint: "bg-emerald-50/60 dark:bg-emerald-950/20",
        badge: "Add Clauses",
        badgeVariant: "default",
    },
    remove_clauses: {
        icon: Minus,
        color: "text-red-500 dark:text-red-400",
        rowTint: "bg-red-50/60 dark:bg-red-950/20",
        badge: "Remove",
        badgeVariant: "destructive",
    },
    update_rationale: {
        icon: Pencil,
        color: "text-blue-500 dark:text-blue-400",
        rowTint: "bg-blue-50/40 dark:bg-blue-950/20",
        badge: "Update",
        badgeVariant: "secondary",
    },
    update_name: {
        icon: Type,
        color: "text-blue-500 dark:text-blue-400",
        rowTint: "bg-blue-50/40 dark:bg-blue-950/20",
        badge: "Rename",
        badgeVariant: "secondary",
    },
    update_description: {
        icon: FileText,
        color: "text-blue-500 dark:text-blue-400",
        rowTint: "bg-blue-50/40 dark:bg-blue-950/20",
        badge: "Update",
        badgeVariant: "secondary",
    },
};

function resolveTopicName(
    entry: { topic_id: number; topic_name?: string | null },
    topicNames: Record<number, string>
): string {
    return (
        entry.topic_name ||
        topicNames[entry.topic_id] ||
        `Topic ${entry.topic_id}`
    );
}

function buildOperations(
    diff: IRefinementDiff,
    topicNames: Record<number, string>
): DiffOperation[] {
    const ops: DiffOperation[] = [];
    const tn = (entry: { topic_id: number; topic_name?: string | null }) =>
        resolveTopicName(entry, topicNames);

    if (diff.update_name) {
        ops.push({
            id: "update_name",
            type: "update_name",
            label: `Rename to "${diff.update_name}"`,
            checked: true,
        });
    }

    if (diff.update_description) {
        ops.push({
            id: "update_description",
            type: "update_description",
            label: "Update description",
            detail: diff.update_description,
            checked: true,
        });
    }

    diff.add_topics?.forEach((entry: IAddTopicEntry, i: number) => {
        const n = entry.enabled_clauses.length;
        ops.push({
            id: `add_topic_${i}`,
            type: "add_topic",
            label: tn(entry),
            detail: `${n} clause${n !== 1 ? "s" : ""} · ${entry.rationale}`,
            checked: true,
        });
    });

    diff.remove_topics?.forEach((entry: IRemoveTopicEntry, i: number) => {
        ops.push({
            id: `remove_topic_${i}`,
            type: "remove_topic",
            label: tn(entry),
            checked: true,
        });
    });

    diff.add_clauses?.forEach((entry: IAddClauseEntry, i: number) => {
        const n = entry.clauses.length;
        ops.push({
            id: `add_clauses_${i}`,
            type: "add_clauses",
            label: `${n} clause${n !== 1 ? "s" : ""} to ${tn(entry)}`,
            checked: true,
        });
    });

    diff.remove_clauses?.forEach((entry: IRemoveClauseEntry, i: number) => {
        const n = entry.topic_clause_ids.length;
        ops.push({
            id: `remove_clauses_${i}`,
            type: "remove_clauses",
            label: `${n} clause${n !== 1 ? "s" : ""} from ${tn(entry)}`,
            checked: true,
        });
    });

    diff.update_topic_rationale?.forEach(
        (entry: IUpdateTopicRationaleEntry, i: number) => {
            ops.push({
                id: `update_rationale_${i}`,
                type: "update_rationale",
                label: `Rationale for ${tn(entry)}`,
                detail: entry.rationale,
                checked: true,
            });
        }
    );

    return ops;
}

function filterDiff(
    diff: IRefinementDiff,
    operations: DiffOperation[]
): IRefinementDiff {
    const checked = new Set(
        operations.filter((o) => o.checked).map((o) => o.id)
    );

    return {
        update_name: checked.has("update_name") ? diff.update_name : undefined,
        update_description: checked.has("update_description")
            ? diff.update_description
            : undefined,
        add_topics: (diff.add_topics ?? []).filter((_, i) =>
            checked.has(`add_topic_${i}`)
        ),
        remove_topics: (diff.remove_topics ?? []).filter((_, i) =>
            checked.has(`remove_topic_${i}`)
        ),
        add_clauses: (diff.add_clauses ?? []).filter((_, i) =>
            checked.has(`add_clauses_${i}`)
        ),
        remove_clauses: (diff.remove_clauses ?? []).filter((_, i) =>
            checked.has(`remove_clauses_${i}`)
        ),
        update_topic_rationale: (diff.update_topic_rationale ?? []).filter(
            (_, i) => checked.has(`update_rationale_${i}`)
        ),
    };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function buildSummary(operations: DiffOperation[]): string {
    const counts: Record<string, number> = {};
    for (const op of operations) {
        if (op.type === "add_topic" || op.type === "add_clauses")
            counts.additions = (counts.additions || 0) + 1;
        else if (op.type === "remove_topic" || op.type === "remove_clauses")
            counts.removals = (counts.removals || 0) + 1;
        else counts.updates = (counts.updates || 0) + 1;
    }
    const parts: string[] = [];
    if (counts.additions)
        parts.push(
            `${counts.additions} addition${counts.additions !== 1 ? "s" : ""}`
        );
    if (counts.removals)
        parts.push(
            `${counts.removals} removal${counts.removals !== 1 ? "s" : ""}`
        );
    if (counts.updates)
        parts.push(
            `${counts.updates} update${counts.updates !== 1 ? "s" : ""}`
        );
    return parts.join(", ");
}

export function DiffPreview({
    diff,
    topicNames = {},
    onApply,
    onReject,
    isApplying = false,
}: DiffPreviewProps) {
    const [operations, setOperations] = useState(() =>
        buildOperations(diff, topicNames)
    );

    const checkedCount = operations.filter((o) => o.checked).length;
    const allChecked = checkedCount === operations.length;
    const noneChecked = checkedCount === 0;
    const summary = buildSummary(operations);

    const toggleOperation = useCallback((id: string) => {
        setOperations((prev) =>
            prev.map((op) =>
                op.id === id ? { ...op, checked: !op.checked } : op
            )
        );
    }, []);

    const toggleAll = useCallback(() => {
        const newChecked = !allChecked;
        setOperations((prev) =>
            prev.map((op) => ({ ...op, checked: newChecked }))
        );
    }, [allChecked]);

    const handleApply = useCallback(() => {
        const filtered = filterDiff(diff, operations);
        onApply(filtered);
    }, [diff, operations, onApply]);

    if (operations.length === 0) {
        return (
            <div className="border-border bg-muted/30 animate-in fade-in slide-in-from-top-2 rounded-lg border px-4 py-6 text-center duration-300">
                <p className="text-muted-foreground text-sm">
                    No changes proposed — the draft already covers this.
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onReject}
                    className="mt-3"
                >
                    Dismiss
                </Button>
            </div>
        );
    }

    return (
        <div className="border-border bg-background animate-in fade-in slide-in-from-top-2 overflow-hidden rounded-lg border shadow-sm duration-300">
            {/* Header */}
            <div className="border-border bg-muted/40 flex items-center justify-between border-b px-4 py-2.5">
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                    <span className="text-foreground text-sm font-semibold">
                        Proposed Changes
                    </span>
                    <span className="text-muted-foreground text-xs">
                        {summary} &middot; {checkedCount}/{operations.length}{" "}
                        selected
                    </span>
                </div>
                <button
                    onClick={toggleAll}
                    className="text-muted-foreground hover:text-foreground text-xs font-medium transition-colors"
                >
                    {allChecked ? "Deselect all" : "Select all"}
                </button>
            </div>

            {/* Operations list */}
            <ul className="divide-border/60 divide-y">
                {operations.map((op) => {
                    const config = OP_CONFIG[op.type];
                    const Icon = config.icon;

                    return (
                        <li
                            key={op.id}
                            className={`flex cursor-pointer items-start gap-3 px-4 py-3 transition-all ${op.checked ? config.rowTint : ""} ${op.checked ? "hover:brightness-95" : "hover:bg-muted/30"} ${!op.checked ? "opacity-40" : ""} `}
                            onClick={() => toggleOperation(op.id)}
                        >
                            <div className="pointer-events-none pt-0.5">
                                <Checkbox checked={op.checked} />
                            </div>
                            <div className={`pt-0.5 ${config.color}`}>
                                <Icon className="size-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                    <Badge variant={config.badgeVariant}>
                                        {config.badge}
                                    </Badge>
                                    <span className="text-foreground truncate text-sm font-medium">
                                        {op.label}
                                    </span>
                                </div>
                                {op.detail && (
                                    <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">
                                        {op.detail}
                                    </p>
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>

            {/* Footer actions */}
            <div className="border-border bg-muted/20 flex items-center justify-end gap-2 border-t px-4 py-2.5">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onReject}
                    disabled={isApplying}
                >
                    <X className="size-3.5" />
                    Dismiss
                </Button>
                <Button
                    variant="default"
                    size="sm"
                    onClick={handleApply}
                    disabled={noneChecked || isApplying}
                >
                    <Check className="size-3.5" />
                    {isApplying
                        ? "Applying..."
                        : `Apply ${checkedCount} Change${checkedCount !== 1 ? "s" : ""}`}
                </Button>
            </div>
        </div>
    );
}
